import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { PageHeader } from '@/components/layout'
import { smtpService, type UpdateSMTPConfigPayload } from '@/services/smtpService'
import { useAuth } from '@/hooks/useAuth'

const smtpSchema = z.object({
  host: z.string().min(1, 'SMTP Host wajib diisi'),
  port: z.coerce.number().int().min(1, 'Port minimal 1').max(65535, 'Port maksimal 65535'),
  user: z.string(),
  password: z.string(),
  fromEmail: z.string().min(1, 'From Email wajib diisi').email('Format email tidak valid'),
  fromName: z.string(),
})

type SMTPFormData = z.infer<typeof smtpSchema>

const MASKED = '****'

export const AdminSMTPSettingsPage: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [passwordTouched, setPasswordTouched] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SMTPFormData>({
    resolver: zodResolver(smtpSchema),
    defaultValues: {
      host: '',
      port: 587,
      user: '',
      password: '',
      fromEmail: '',
      fromName: '',
    },
  })

  useEffect(() => {
    let mounted = true
    smtpService
      .getSMTPConfig()
      .then((res) => {
        if (!mounted) return
        if (res.success && res.data) {
          reset({
            host: res.data.host || '',
            port: res.data.port || 587,
            user: res.data.user || '',
            password: res.data.password || '',
            fromEmail: res.data.fromEmail || '',
            fromName: res.data.fromName || '',
          })
        }
      })
      .catch((err) => {
        if (mounted) setToast({ type: 'error', text: (err as Error).message })
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [reset])

  const onSubmit = async (data: SMTPFormData) => {
    setSaving(true)
    setToast(null)
    try {
      const payload: UpdateSMTPConfigPayload = {
        host: data.host,
        port: data.port,
        user: data.user,
        fromEmail: data.fromEmail,
        fromName: data.fromName,
      }
      if (passwordTouched && data.password !== MASKED) {
        payload.password = data.password
      }
      const res = await smtpService.updateSMTPConfig(payload)
      if (!res.success) throw new Error(res.error?.message || 'Gagal menyimpan')
      setToast({ type: 'success', text: 'Konfigurasi SMTP tersimpan' })
      setPasswordTouched(false)
    } catch (err) {
      setToast({ type: 'error', text: (err as Error).message || 'Gagal menyimpan' })
    } finally {
      setSaving(false)
    }
  }

  const handleTestEmail = async () => {
    setTesting(true)
    setToast(null)
    try {
      const res = await smtpService.testSMTPConfig()
      if (!res.success) throw new Error(res.error?.message || 'Gagal mengirim test email')
      setToast({ type: 'success', text: res.data?.message || `Test email dikirim ke ${user?.email}` })
    } catch (err) {
      setToast({ type: 'error', text: (err as Error).message || 'Gagal mengirim test email' })
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-white/10 rounded w-64" />
          <div className="h-64 bg-gray-200 dark:bg-white/10 rounded-xl" />
        </div>
      </div>
    )
  }

  return (
    <div data-testid="admin-smtp-settings-page">
      <PageHeader
        title="SMTP Settings"
        subtitle="Konfigurasi server email untuk pengiriman notifikasi"
      />

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            toast.type === 'success'
              ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
              : 'bg-red-500/10 text-red-500 border border-red-500/20'
          }`}
        >
          <span className="material-symbols-outlined text-[20px]">
            {toast.type === 'success' ? 'check_circle' : 'error'}
          </span>
          <span className="font-medium">{toast.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl">
        <div className="bg-white dark:bg-surface-card rounded-lg border border-slate-200 dark:border-white/5 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/[0.02]">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">mail</span>
              Konfigurasi SMTP Server
            </h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="smtp-host" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  SMTP Host
                </label>
                <input
                  id="smtp-host"
                  type="text"
                  {...register('host')}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary/50 transition-all"
                  placeholder="smtp.example.com"
                  data-testid="smtp-host"
                />
                {errors.host && (
                  <p className="mt-1 text-xs text-red-500">{errors.host.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="smtp-port" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  Port
                </label>
                <input
                  id="smtp-port"
                  type="number"
                  min={1}
                  max={65535}
                  {...register('port')}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white text-sm font-mono focus:outline-none focus:border-primary/50 transition-all"
                  data-testid="smtp-port"
                />
                {errors.port && (
                  <p className="mt-1 text-xs text-red-500">{errors.port.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="smtp-user" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  SMTP User
                </label>
                <input
                  id="smtp-user"
                  type="text"
                  {...register('user')}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary/50 transition-all"
                  placeholder="user@example.com"
                  data-testid="smtp-user"
                />
                {errors.user && (
                  <p className="mt-1 text-xs text-red-500">{errors.user.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="smtp-password" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  Password
                </label>
                <input
                  id="smtp-password"
                  type="text"
                  {...register('password')}
                  onFocus={() => setPasswordTouched(true)}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white text-sm font-mono focus:outline-none focus:border-primary/50 transition-all"
                  placeholder="Kosongkan jika tidak diubah"
                  data-testid="smtp-password"
                />
                <p className="mt-1 text-[11px] text-slate-400 italic">Biarkan **** jika tidak ingin mengubah password</p>
                {errors.password && (
                  <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="smtp-from-email" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  From Email
                </label>
                <input
                  id="smtp-from-email"
                  type="email"
                  {...register('fromEmail')}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary/50 transition-all"
                  placeholder="no-reply@example.com"
                  data-testid="smtp-from-email"
                />
                {errors.fromEmail && (
                  <p className="mt-1 text-xs text-red-500">{errors.fromEmail.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="smtp-from-name" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  From Name
                </label>
                <input
                  id="smtp-from-name"
                  type="text"
                  {...register('fromName')}
                  className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary/50 transition-all"
                  placeholder="Pelatihan Merdeka"
                  data-testid="smtp-from-name"
                />
                {errors.fromName && (
                  <p className="mt-1 text-xs text-red-500">{errors.fromName.message}</p>
                )}
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100 dark:border-white/5 flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={handleTestEmail}
                disabled={testing}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-slate-300 rounded-lg font-bold text-sm hover:bg-slate-200 dark:hover:bg-white/15 transition-all disabled:opacity-50"
                data-testid="smtp-test-button"
              >
                <span className="material-symbols-outlined text-[20px]">send</span>
                {testing ? 'Mengirim...' : 'Kirim Test Email'}
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 px-8 py-2.5 bg-primary text-white rounded-lg font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                data-testid="smtp-save-button"
              >
                <span className="material-symbols-outlined text-[20px]">save</span>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default AdminSMTPSettingsPage
